import type { ContentHref } from '@generalTypes/apiTypes';
import { expandLlinkidGoalRelations } from '@newStore/llinkid/llinkidActions';
import type { EditAsideGoalRelations } from '@nodeTypeConfig/configTypes';
import { Modal } from '@kathondvla/react-shared-components/src/components';
import ModalErrorBoundary from '@UI/modals/ModalErrorBoundary';
import { getResourceKey } from '@store/helpers/documentHelpers';
import { any } from 'prop-types';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import LlinkidGoalOdetEndTermReferencesModal from '@UI/modals/LlinkidGoalOdetEndTermReferencesModal';
import { addRelationAction, patchRelationAction } from '@store/actions/documentActions';
import { useEditHref } from '../../../hooks/UseEditHref';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import AsideLlinkidGoal from '../asideLlinkidGoal/asideLlinkidGoal';
import AsideRelationsPanel from '../asideRelationsPanel/AsideRelationsPanel';
import { useDeleteRelationHook } from '../asideRelationsPanel/useDeleteRelationHook';
import { selectAsideGoalRelations } from './asideGoalRelationsSelectors';
import LlinkidGoalRelationsModal from '@UI/modals/LlinkidGoalRelationsModal';

const AsideGoalRelations: React.FC<{ config: EditAsideGoalRelations; readonly: boolean }> = ({
  config,
  readonly,
}) => {
  const dispatch = useDispatch();
  const currentEditingHref = useEditHref();
  const llinkidGoalOdetEndTermReferencesModal = useRef<Modal>();
  const llinkidGoalRelationsModal = useRef<Modal>();

  const multipleRelationTypes = config.options.relationTypes.length > 1;
  const relationOrigin = !config.options.revertedRelationDirection ? 'from' : 'to';
  const relationEnd = !config.options.revertedRelationDirection ? 'to' : 'from';
  const isRelationTypeRelationIncluded = config.options.relationTypes.includes('RELATION');

  const relations = useTypedSelector((State) =>
    selectAsideGoalRelations(State, currentEditingHref, config),
  );
  const goalRelationsParams = {
    key: getResourceKey(currentEditingHref),
    relationTypes: config.options.relationTypes,
    originPart: relationOrigin,
  };

  const currentModal = config.options.modal === 'llinkidGoalRelationsModal'
      ? llinkidGoalRelationsModal : llinkidGoalOdetEndTermReferencesModal;
  const onDelete = useDeleteRelationHook();

  const onCreate = async () => {
    const selectedValues = relations.map((rel) => getResourceKey(rel[relationEnd]?.href));

    currentModal.current?.open({
      selectedValues,
      multipleRelationTypes,
      onSubmit: (response: { goalHref: ContentHref, realisation: string }) => {
        if (response) {
          const newRelation = {
            relationtype: multipleRelationTypes
            ? response.realisation
          : config.options.relationTypes[0],
            strength: 'MEDIUM',
            [relationOrigin]: {
              href: currentEditingHref,
            },
            [relationEnd]: {
              href: response.goalHref,
            },
          };

          // @ts-expect-error addRelationAction is not typed
          dispatch(addRelationAction(newRelation, true, true, goalRelationsParams));
        }
      },
    });
  };

  const onEdit = async (relation) => {
    if (readonly) {
      return;
    }

    const selectedValues = relations.map((rel) => getResourceKey(rel[relationEnd]?.href));

    currentModal.current?.open({
      selectedValues,
      multipleRelationTypes,
      item: relation,
      onSubmit: (goal: { $$meta: { permalink: string } }) => {
        if (goal) {
          dispatch(
            patchRelationAction(
              relation.key,
              {
                [relation.$$reversed ? relationOrigin : relationEnd]: {
                  href: goal.$$meta.permalink,
                  $$expanded: goal,
                },
                relationtype: config.options.relationTypes[0],
              },
              {
                ...goalRelationsParams,
                ...(relation.$$reversed && { originPart: relationEnd }),
              }
            )
          );
        }
      },
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    dispatch(
      expandLlinkidGoalRelations({
        key: getResourceKey(currentEditingHref),
        relationTypes: config.options.relationTypes,
        originPart: relationOrigin,
      }),
    );

    // special case of RELATION relations where we also need to fetch the from in the to relations
    if (isRelationTypeRelationIncluded) {
      dispatch(
        expandLlinkidGoalRelations({
          key: getResourceKey(currentEditingHref),
          relationTypes: ['RELATION'],
          originPart: relationEnd,
        }),
      );
    }
  }, []);

  return (
    <>
      <AsideRelationsPanel
        title={config.label}
        items={relations}
        readOnly={readonly}
        onCreate={onCreate}
        onDelete={onDelete}
        onEdit={multipleRelationTypes ? onEdit : null} // only allow editing if there is something to edit, like the type of the relation.
      >
        {(item) => (
          <AsideLlinkidGoal
            item={item}
            options={config.options}
            href={item[relationEnd].href as ContentHref}
          />
        )}
      </AsideRelationsPanel>
      <Modal
        ref={llinkidGoalOdetEndTermReferencesModal}
        size="sm"
        component={<LlinkidGoalOdetEndTermReferencesModal />}
        errorBoundaryComponent={ModalErrorBoundary}
      />
      <Modal
        ref={llinkidGoalRelationsModal}
        size="sm"
        component={<LlinkidGoalRelationsModal />}
        errorBoundaryComponent={ModalErrorBoundary}
      />
    </>
  );
};

AsideGoalRelations.propTypes = {
  config: any,
  readonly: any,
};

AsideGoalRelations.displayName = 'AsideGoalRelations';

export default AsideGoalRelations;
